"use client";

import React from "react";
import { Footer } from "@/components/LandingLayout";
import { 
  BookOpen, 
  Terminal, 
  Key, 
  Info, 
  ShieldCheck,
  Zap,
  Copy
} from "lucide-react";

export default function DocsPage() {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 font-sans antialiased transition-colors duration-300">

      <main className="container mx-auto px-4 max-w-5xl pt-20 pb-20">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar Navigation */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-8">
              <div>
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 dark:text-zinc-500 mb-4">Getting Started</h4>
                <ul className="space-y-2">
                  <li><a href="#authentication" className="text-xs font-bold text-blue-600 dark:text-blue-500 hover:text-blue-500 dark:hover:text-blue-400 block py-1 transition-colors">Authentication</a></li>
                  <li><a href="#base-url" className="text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white block py-1 transition-colors">Base URL</a></li>
                  <li><a href="#rate-limits" className="text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white block py-1 transition-colors">Rate Limits</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 dark:text-zinc-500 mb-4">Endpoints</h4>
                <ul className="space-y-2">
                  <li><a href="#extract" className="text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white block py-1 transition-colors">Extraction</a></li>
                </ul>
              </div>
            </div>
          </aside>

          {/* Documentation Content */}
          <div className="flex-1 min-w-0">
            <header className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-blue-600/10 flex items-center justify-center border border-blue-600/20">
                  <BookOpen className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                </div>
                <h1 className="text-3xl font-black tracking-tight text-zinc-900 dark:text-white">API Documentation</h1>
              </div>
              <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed max-w-2xl">
                Our REST API provides programmatic access to the TeraBox extraction engine. 
                One simple endpoint handles metadata, download links, and stream URLs.
              </p>
            </header>

            {/* Authentication */}
            <section id="authentication" className="mb-16 scroll-mt-24">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2 text-zinc-900 dark:text-white">
                <Key className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                Authentication
              </h2>
              <p className="text-zinc-600 dark:text-zinc-400 text-sm mb-6 leading-relaxed">
                All requests to the API must include your API Key in the <code className="text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-400/5 px-1.5 py-0.5 rounded text-[11px]">X-API-KEY</code> header.
              </p>
              <div className="bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-5 relative group">
                <pre className="text-xs text-zinc-700 dark:text-zinc-300 font-mono overflow-x-auto">
                  <code>{`GET /api/terabox/extract HTTP/1.1\nHost: api-v2.teraboxdl.site\nX-API-KEY: YOUR_API_KEY_HERE`}</code>
                </pre>
                <button 
                  onClick={() => copyToClipboard("X-API-KEY: YOUR_API_KEY_HERE")}
                  className="absolute top-4 right-4 p-2 bg-zinc-200 dark:bg-zinc-800 rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-zinc-300 dark:hover:bg-zinc-700"
                >
                  <Copy className="w-3 h-3 text-zinc-500 dark:text-zinc-400" />
                </button>
              </div>
            </section>

            {/* Base URL */}
            <section id="base-url" className="mb-16 scroll-mt-24">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2 text-zinc-900 dark:text-white">
                <Terminal className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                Base URL
              </h2>
              <div className="bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-5 shadow-sm dark:shadow-none">
                <code className="text-xs font-bold text-blue-600 dark:text-blue-400 break-all select-all">https://api-v2.teraboxdl.site</code>
              </div>
            </section>

            {/* Extraction */}
            <section id="extract" className="mb-16 scroll-mt-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold flex items-center gap-2 text-zinc-900 dark:text-white">
                  <Zap className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                  Extraction
                </h2>
                <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 text-[10px] font-black uppercase tracking-widest border border-blue-500/20">POST</span>
              </div>
              <p className="text-zinc-600 dark:text-zinc-400 text-sm mb-6 leading-relaxed">
                Extract metadata, download links, and stream URLs for any TeraBox share link.
              </p>
              <h4 className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-3">Request Body</h4>
              <div className="bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-5 mb-6">
                <pre className="text-xs text-zinc-700 dark:text-zinc-300 font-mono">
                  <code>{`{\n  "url": "https://terabox.com/s/1abc...",\n  "dir_path": "/Optional/Subdirectory",\n  "page": 1\n}`}</code>
                </pre>
              </div>
              <h4 className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-3">Response Schema</h4>
              <div className="bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-5">
                <pre className="text-xs text-zinc-700 dark:text-zinc-300 font-mono overflow-x-auto">
                  <code>{`{\n  "errno": 0,\n  "request_id": "921823...",\n  "list": [\n    {\n      "fs_id": "102...",\n      "server_filename": "example.mp4",\n      "size": 102400,\n      "dlink": "https://...",\n      "stream_url": "https://..."\n    }\n  ]\n}`}</code>
                </pre>
              </div>
            </section>

            {/* Rate Limits */}
            <section id="rate-limits" className="mb-20 scroll-mt-24">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2 text-zinc-900 dark:text-white">
                <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-500" />
                Rate Limiting
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl">
                  <h4 className="text-[10px] font-black text-zinc-400 dark:text-zinc-500 uppercase mb-2">Free Plan</h4>
                  <div className="text-xl font-black text-zinc-900 dark:text-white">100 req/mo</div>
                </div>
                <div className="p-4 bg-blue-600 rounded-xl shadow-lg shadow-blue-600/20">
                  <h4 className="text-[10px] font-black text-blue-200 uppercase mb-2">Standard Plan</h4>
                  <div className="text-xl font-black text-white">Unlimited</div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
